import { base64ToUint8Array, uint8ArrayToString } from '../Converter';
import * as apiWrapper from './Wrapper';

// Submodules
import { assert } from '../../utils/Debug';

import utils = egr.wcf.utils;

export const ChunkSize: number = 2097152; // 2mb

export type Brakesman = () => Promise<void>;

const LockMap: Map<string, utils.async.Lock> = new Map<string, utils.async.Lock>();
async function lockFile(path: string): Promise<VoidFunction> {
    let lock: utils.async.Lock | undefined = LockMap.get(path);
    if (lock == null) {
        lock = new utils.async.Lock();
        LockMap.set(path, lock);
    }

    // Note: the function only ensures that the write operations do not overwrite each
    // other. However, it is probably wrong to write to the same file more than once at
    // the same time.
    assert(lock.isLocked === false, 'File should not be logged', path);

    const context: utils.async.LockContext = await lock.acquireLock();
    return (): void => {
        context.unlock();

        if (lock != null && !lock.isLocked) {
            LockMap.delete(path);
        }
    };
}

export function isDirectory(path: string): boolean {
    return path.endsWith('/');
}

export function isFile(path: string): boolean {
    return !isDirectory(path);
}

export function stripTrailingSlash(path: string): string {
    return path.replace(/\/+$/, '');
}

export async function getSubDirectories(path: string, trailingSlash: boolean = true): Promise<Array<string>> {
    const entries: Array<string> = await apiWrapper.list(path);

    const directories: Array<string> = entries.filter(isDirectory);

    if (trailingSlash === false) {
        return directories.map(stripTrailingSlash);
    }

    return directories;
}

export async function readTextFile(path: string, brakesman?: Brakesman): Promise<string> {
    let fd: string;
    try {
        fd = await apiWrapper.open(path, 'r', '');
    } catch (_) {
        // no souch file
        return null!; // FixMe -> change function signature or calling code
    }

    let data: string = '';
    while (true) {
        if (brakesman != null) {
            await brakesman();
        }

        const chunk: string = await apiWrapper.read(fd, ChunkSize);
        data += chunk;

        if (chunk.length < ChunkSize) {
            void apiWrapper.close(fd); // close async
            return data;
        }
    }
}

export async function readBinaryFile(path: string, brakesman?: Brakesman): Promise<ArrayBuffer> {
    let fd: string;
    try {
        fd = await apiWrapper.open(path, 'r', 'base64');
    } catch (_) {
        // no souch file
        return null!; // FixMe -> change function signature or calling code
    }

    let data: Uint8Array = new Uint8Array(0);
    while (true) {
        if (brakesman != null) {
            await brakesman();
        }

        const chunk: Uint8Array = base64ToUint8Array(
            await apiWrapper.read(fd, ChunkSize)
        );

        if (data.byteLength === 0 && chunk.byteLength < ChunkSize) {
            void apiWrapper.close(fd); // close async
            return chunk.buffer;
        }

        const newData: Uint8Array = new Uint8Array(data.byteLength + chunk.byteLength);
        newData.set(data, 0);
        newData.set(chunk, data.byteLength);

        data = newData;

        if (chunk.byteLength !== ChunkSize) {
            void apiWrapper.close(fd); // close async
            return data.buffer;
        }
    }
}

export async function writeFile(path: string, data: ArrayBuffer | string, brakesman: Brakesman | null = null): Promise<void> {
    const unlock: VoidFunction = await lockFile(path);
    try {
        return await _writeFile(path, data, brakesman);
    } finally {
        unlock();
    }
}

async function _writeFile(path: string, data: ArrayBuffer | string, brakesman: Brakesman | null = null): Promise<void> {
    let fd: string | null = null;
    try {
        if (data instanceof ArrayBuffer) {
            fd = await apiWrapper.open(path, 'w', 'base64');
            for (let i: number = 0, l: number = data.byteLength; i < l; i +=  ChunkSize) {
                if (brakesman != null) {
                    await brakesman();
                }

                await apiWrapper.write(
                    fd,
                    btoa(
                        uint8ArrayToString(
                            new Uint8Array(data.slice(i, i + ChunkSize))
                        )
                    )
                );
            }
        } else {
            fd = await apiWrapper.open(path, 'w', '');
            for (let i: number = 0, l: number = data.length; i < l; i +=  ChunkSize) {
                if (brakesman != null) {
                    await brakesman();
                }

                await apiWrapper.write(
                    fd,
                    data.slice(i, i + ChunkSize)
                );
            }
        }

        return apiWrapper.close(fd);
    } catch (error) {
        if (fd != null) {
            try {
                await apiWrapper.close(fd);
            } catch (_) {
                // ignore error
            }
        }

        return Promise.reject(error);
    }
}

export async function* findAllFiles(path: string, maxdepth: number = 1000): AsyncIterableIterator<string> {
    if (maxdepth < 1) {
        return;
    }

    try {
        const entries: Array<string> = await apiWrapper.list(path);
        for (const entry of entries) {
            if (isDirectory(entry)) {
                yield* findAllFiles(path + entry, maxdepth - 1);
            } else {
                yield path + entry;
            }
        }
    } catch {
        // FixMe: catch only `no such directory` exceptions
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/submodules/app-api/Filesystem/Helper.ts